import { Flex, Tooltip } from 'antd';
import { NavLink, Outlet, useParams } from 'react-router-dom';

import { type CompanyTabPath, canOpenCompanyTab } from '@constants';
import { useAccount } from '@hooks';
import useBackofficeTransactionsAccess from '@hooks/useBackofficeTransactionsAccess';

import { useDriveLink } from './hooks/useDriveLink';
import * as S from './styled';

type CompanyLink = {
  // Typed against the route table, so a tab pointing at a suffix with no route stops compiling
  // instead of rendering disabled for everyone.
  link: CompanyTabPath;
  label: string;
  end?: boolean;
};

const NOT_FOR_YOUR_ROLE = 'Not available for your role';

const LINKS: CompanyLink[] = [
  {
    link: '',
    label: 'Onboarding',
    end: true,
  },
  {
    link: '/info',
    label: 'Company info',
  },
  {
    link: '/transactions',
    label: 'Transactions',
  },
  {
    link: '/documents',
    label: 'Documents',
  },
  {
    link: '/billing',
    label: 'Billing',
  },
  {
    link: '/accounts',
    label: 'Accounts',
  },
  {
    link: '/legal',
    label: 'Corp.Doc',
  },
  {
    link: '/payslips',
    label: 'Payslips',
  },
  {
    link: '/taxes',
    label: 'VAT & Taxes',
  },
  {
    link: '/team',
    label: 'Team',
  },
];

const LayoutCompany = () => {
  const { companyId } = useParams<{ companyId: string }>();
  const { role } = useAccount();
  const driveLink = useDriveLink(companyId || '');
  const { isBlocked } = useBackofficeTransactionsAccess({ companyId });

  // Tabs the role cannot open stay in the bar but are disabled, so the set of tabs no longer
  // depends on who is looking — only whether they are reachable [ENG-1197]. A company-level
  // transactions block is a per-person restriction rather than a role one, so it keeps hiding.
  const visibleLinks = LINKS.filter((link) => !(isBlocked && link.link === '/transactions'));

  return (
    <S.Root gap={16} vertical>
      {companyId && (
        <Flex gap="4px 32px" wrap="wrap">
          {visibleLinks.map((link) =>
            canOpenCompanyTab(link.link, role) ? (
              <NavLink
                key={link.link}
                end={link.end}
                to={`/companies/${companyId}${link.link}`}
              >
                {({ isActive }) => (
                  <S.Title isActive={isActive} level={5}>
                    {link.label}
                  </S.Title>
                )}
              </NavLink>
            ) : (
              // A disabled tab is not a link — no role="link", which would announce an
              // affordance that isn't there — so it is kept in the tab order with an explicit
              // focus trigger instead: antd only opens tooltips on hover by default, which would
              // leave keyboard users with no way to learn why the tab does nothing.
              <Tooltip
                key={link.link}
                title={NOT_FOR_YOUR_ROLE}
                trigger={['hover', 'focus']}
              >
                <S.Title
                  aria-disabled="true"
                  isActive={false}
                  isDisabled
                  level={5}
                  tabIndex={0}
                >
                  {link.label}
                </S.Title>
              </Tooltip>
            )
          )}
          <NavLink to={driveLink} target="_blank">
            {({ isActive }) => (
              <S.Title isActive={isActive} level={5}>
                Drive
              </S.Title>
            )}
          </NavLink>
        </Flex>
      )}
      <Outlet />
    </S.Root>
  );
};

export default LayoutCompany;
